import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { StickyNote, Check } from "lucide-react";
import { useBrowserKeyboardInset } from "../capacitor/keyboardInset";
import { setNativeAutofill } from "../capacitor/clarityBars";
import { showKeyboardNow } from "../capacitor/keyboardNow";
import { pushBackHandler } from "../capacitor/backButton";
import { SESSION_NOTE_MAX } from "../utils/sessionGrouping";


// A logged Clock-In session's note (Jade, 2026-10-10, from a Play review of
// another app: "let me add a quick note to a finished time entry").

export const NOTE_ADD_LABEL = "Add note";
export const NOTE_PLACEHOLDER = "How did it go?";

// The small bordered "Add note" button. It sits in a spot that's already there
// (the panel header, or the end of a log line), so the panel doesn't grow a
// row just to offer it (Jade).
export function NoteAddPill({ onClick, small = false, title }) {
  return (
    <button type="button" onClick={onClick} title={title || NOTE_ADD_LABEL}
      style={{
        display: "inline-flex", alignItems: "center", gap: small ? 4 : 5, flexShrink: 0,
        padding: small ? "2px 8px" : "4px 10px", borderRadius: 99,
        border: "1.5px solid var(--c-primary)", background: "transparent", cursor: "pointer",
        fontFamily: "inherit", fontSize: small ? 11.5 : 12.5, fontWeight: 700,
        color: "var(--c-text-accent)", whiteSpace: "nowrap", lineHeight: 1.3, minHeight: 0, height: "auto",
      }}>
      <StickyNote size={small ? 11 : 13} /> {NOTE_ADD_LABEL}
    </button>
  );
}

// The note box: a bar sitting directly on top of the keyboard, like the
// Planner's quick-add bar (Jade, 2026-10-10 — not a centred panel). A small
// label says which session it's for. Enter or the round button saves; tapping
// the dim saves too (so typed text is never lost); Back / Escape cancels.
// Emptying a note and saving removes it.
export function SessionNoteBar({ note = "", label = "", onSave, onClose }) {
  const [draft, setDraft] = useState(note || "");
  const kbInset = useBrowserKeyboardInset();
  const inputRef = useRef(null);
  const save = () => {
    if ((draft || "").trim() !== (note || "").trim()) onSave(draft);
    onClose();
  };
  const saveRef = useRef(save);
  saveRef.current = save;

  // Open with the keyboard up (the WebView sometimes ignores the first focus
  // during the opening tap, hence the retry).
  useEffect(() => {
    const focus = () => {
      const el = inputRef.current;
      if (!el) return;
      el.focus({ preventScroll: true });
      const end = el.value.length; el.setSelectionRange(end, end);
      showKeyboardNow(); // not Keyboard.show(): that one waits 350ms
    };
    focus();
    const t = setTimeout(focus, 60);
    return () => clearTimeout(t);
  }, []);
  // No Google autofill strip above the keyboard (same as the quick-add bar).
  useEffect(() => { setNativeAutofill(false); return () => setNativeAutofill(true); }, []);
  useEffect(() => pushBackHandler(() => { onClose(); return true; }), [onClose]);
  // Grow with the text, up to about four lines.
  useLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 112)}px`;
  }, [draft]);
  const keepFocus = e => e.preventDefault();

  return createPortal(
    <div style={{ position: "fixed", inset: 0, zIndex: 100001, fontFamily: "'Quicksand','Nunito',sans-serif" }}>
      <style>{"textarea.note-plain:focus { box-shadow: none !important; outline: none !important; border-color: transparent !important; }"}</style>
      <div onClick={() => saveRef.current()} style={{ position: "absolute", inset: 0, background: "rgba(60,30,50,0.32)" }} />
      <div role="dialog" aria-label={NOTE_ADD_LABEL}
        style={{
          position: "absolute", left: 0, right: 0, bottom: kbInset,
          background: "var(--c-bg-card)", borderRadius: "22px 22px 0 0",
          boxShadow: "0 -6px 28px rgba(60,30,50,0.16)",
          padding: "12px 14px calc(10px + var(--sab, 0px))",
        }}>
        <p style={{ margin: "0 0 8px 4px", fontSize: 12.5, fontWeight: 700, color: "var(--c-text-accent)", display: "flex", alignItems: "center", gap: 5, overflow: "hidden", whiteSpace: "nowrap" }}>
          <StickyNote size={13} style={{ flexShrink: 0 }} />
          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{note ? "Edit note" : NOTE_ADD_LABEL}{label ? ` · ${label}` : ""}</span>
        </p>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 10 }}>
          <textarea ref={inputRef} className="note-plain" rows={1} value={draft} maxLength={SESSION_NOTE_MAX}
            placeholder={NOTE_PLACEHOLDER} enterKeyHint="done" autoComplete="off"
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); save(); }
              if (e.key === "Escape") onClose();
            }}
            style={{
              flex: 1, minWidth: 0, minHeight: 46, boxSizing: "border-box", resize: "none",
              border: "none", outline: "none", boxShadow: "none", borderRadius: 14,
              padding: "12px 14px", background: "var(--c-bg-active)", color: "var(--c-text)",
              fontFamily: "inherit", fontSize: 16, fontWeight: 600, lineHeight: 1.4,
            }} />
          <button type="button" aria-label="Save note" onMouseDown={keepFocus} onClick={save}
            style={{
              width: 46, height: 46, minHeight: 0, borderRadius: "50%", border: "none", flexShrink: 0, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              background: "linear-gradient(135deg, var(--c-primary-light), var(--c-primary-deep))", color: "var(--c-on-primary, #fff)",
            }}>
            <Check size={22} strokeWidth={3} />
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
